import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import {
  BodyText,
  Button,
  ListActionCollapsible,
  TextField,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import { CreditMenuItem } from '@order/components';
import { usePromoCode } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import type { ModalStackParamList } from '../../../../../../navigation/AppNavigation.props';
import type { useCheckoutContext } from '../../../../CheckoutProvider';

/**
 * Input field for adding promo codes without the separation of gift cards.
 * Used when `CELS-1410-separate-promo-and-gift-card` is disabled.
 * Remains purposefully unchanged from the previous implementation.
 * Will be deleted once we remove this feature flag.
 */
export const CheckoutPaymentBlockPromoCodeWithoutSeparateGiftCard = (
  props: Pick<ReturnType<typeof useCheckoutContext>, 'refetchCredits'>,
) => {
  const { refetchCredits } = props;

  const {
    promoCode,
    isSubmittingPromoCode,
    promoCodeInvalid: hasPromoCodeError,
    isPromoCodeApplied,
    setPromoCode,
    submitPromoCode: onPromoCodeSubmit,
  } = usePromoCode(refetchCredits);

  const navigation =
    useNavigation<NativeStackNavigationProp<ModalStackParamList>>();

  const handlePressCreditAdd = useCallback(() => {
    navigation.navigate('CreditAdd');
  }, [navigation]);

  const shouldDisableApplyBtn = promoCode.trim() === '';

  const { currentBreakpoint } = useResponsive();
  const { t } = useLocalizationContext();

  if (currentBreakpoint.isXS) {
    return (
      <CreditMenuItem
        testID="account.open-promo-code-selection-button"
        accessibilityLabel={t('account.credit.a11y-label')}
        accessibilityHint={t('account.credit.a11y-label')}
        iconName="IconPromo"
        title={t('account.credit.field-placeholder')}
        onPress={handlePressCreditAdd}
      />
    );
  }

  const promoCodeAlreadyAppliedNotice = isPromoCodeApplied
    ? t('account.notice.credit-already-applied')
    : undefined;

  const invalidPromoCodeNotice = hasPromoCodeError
    ? t('account.notice.credit-invalid')
    : undefined;

  return (
    <ListActionCollapsible
      iconName="IconPromo"
      text={t('checkout.add-gift-card-code')}
    >
      <View>
        <View style={styles.promoCodeWrapper}>
          <View style={styles.promoCodeInputWrapper}>
            <TextField
              placeholder={t('checkout.add-gift-card-code')}
              accessibilityLabel={t('checkout.add-gift-card-code')}
              value={promoCode}
              onChangeText={setPromoCode}
              notice={promoCodeAlreadyAppliedNotice ?? invalidPromoCodeNotice}
              invalid={hasPromoCodeError}
              noticePalette="caution"
            />
          </View>

          <Button
            palette="primary"
            accessibilityLabel={t('checkout.payment.promo-code.apply')}
            style={styles.applyButton}
            disabled={shouldDisableApplyBtn}
            isLoading={isSubmittingPromoCode}
            onPress={onPromoCodeSubmit}
          >
            {t('general.apply')}
          </Button>
        </View>

        <BodyText size={4}>{t('checkout.gift-card-note')}</BodyText>
      </View>
    </ListActionCollapsible>
  );
};

const styles = StyleSheet.create({
  promoCodeWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing['4'],
  },
  promoCodeInputWrapper: {
    flexGrow: 1,
    paddingRight: theme.spacing['8'],
    marginTop: theme.spacing['6'],
  },
  applyButton: {
    width: 88,
  },
});
